// ─── In-app confirmation & message dialogs ───────────────────────────────────
//   const ok = await uiConfirm('Delete this supplier?');                 // plain text, like window.confirm
//   const ok = await uiConfirm({ title, message, confirmText, danger }); // full control
// Classic interface → the browser's own window.confirm / window.alert (unchanged behaviour).
// Any other interface style → styled dialog; Enter confirms, Esc / backdrop cancels.
// window.alert is also routed here (installAlertDialog, called once from index.js).
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { isModernUi } from '../../utils/uiMode';

const VERBS = ['Delete', 'Remove', 'Reverse', 'Post', 'Approve', 'Reject', 'Undo', 'Discard', 'Reset', 'Close', 'Submit', 'Void',
  'Import', 'Restore', 'Deactivate', 'Activate', 'Clear', 'Run', 'Process', 'Lock', 'Unlock', 'Send', 'Save', 'Overwrite', 'Replace',
  'Generate', 'Mark', 'Release', 'Cancel', 'Revert', 'Purge', 'Archive', 'Terminate', 'Finalize', 'Finalise', 'Confirm', 'Proceed', 'Apply', 'Continue'];
const DANGER_RE = /\b(delete|remove|reverse|reversal|void|discard|purge|terminate|cannot be undone|permanently|reject|wipe|erase|overwrite|reset)\b/i;

// Turn a window.confirm-style text into title + message + button label
function fromText(text) {
  const raw = String(text ?? '').replace(/\r/g, '').trim();
  const lines = raw.split('\n');
  let title = lines[0].trim(), message = lines.slice(1).join('\n').trim();
  if (!message) {                                                    // "Delete X? This cannot be undone." → title + detail
    const q = title.match(/^(.{6,120}?\?)\s+(\S.+)$/s);
    if (q) { title = q[1]; message = q[2]; }
  }
  if (!message && title.length > 90) {                               // one long sentence → split after the first question / sentence
    const m = title.match(/^(.{10,90}?[?.!])\s+(.+)$/s);
    if (m) { title = m[1]; message = m[2]; } else { message = title; title = 'Please confirm'; }
  }
  const first = (title.match(/^[A-Za-z]+/) || [''])[0];
  const verb = VERBS.find(v => v.toLowerCase() === first.toLowerCase());
  return {
    title, message,
    confirmText: !verb ? 'OK' : verb === 'Cancel' ? 'Yes, cancel' : verb === 'Confirm' || verb === 'Proceed' || verb === 'Continue' ? verb : verb,
    cancelText: verb === 'Cancel' ? 'Keep' : 'Cancel',
    danger: DANGER_RE.test(raw),
  };
}

function Dialog({ title, message, confirmText, cancelText, danger, alertOnly, onClose }) {
  const okRef = useRef(null);
  useEffect(() => {
    okRef.current?.focus();
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(false); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onClose]);
  const lines = String(message || '').split('\n');
  return (
    <div className="ui-confirm-overlay" onMouseDown={() => onClose(false)}>
      <div className="ui-confirm" role="alertdialog" aria-modal="true" aria-labelledby="ui-confirm-title" onMouseDown={e => e.stopPropagation()}>
        <div className="ui-confirm-body">
          <div className={`ui-confirm-mark${danger ? ' danger' : ''}`} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {danger ? <><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></>
                      : <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></>}
            </svg>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div id="ui-confirm-title" className="ui-confirm-title">{title}</div>
            {lines.map((l, i) => l.trim() ? <p key={i} className="ui-alert-msg">{l}</p> : (i > 0 && i < lines.length - 1 ? <div key={i} style={{ height: 6 }} /> : null))}
          </div>
        </div>
        <div className="ui-confirm-foot">
          {!alertOnly && <button type="button" className="ui-confirm-btn" onClick={() => onClose(false)}>{cancelText}</button>}
          <button type="button" ref={okRef} className={`ui-confirm-btn ${danger && !alertOnly ? 'danger' : 'primary'}`} onClick={() => onClose(true)}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

function mount(props) {
  return new Promise(resolve => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    let done = false;
    const close = (v) => { if (done) return; done = true; root.unmount(); host.remove(); resolve(v); };
    root.render(<Dialog {...props} onClose={close} />);
  });
}

export function uiConfirm(opts = {}) {
  const isText = typeof opts !== 'object' || opts === null;
  if (!isModernUi()) {
    if (isText) return Promise.resolve(window.confirm(opts));
    return Promise.resolve(window.confirm([opts.title, opts.message].filter(Boolean).join('\n\n')));
  }
  const o = isText ? fromText(opts) : { title: 'Please confirm', message: '', confirmText: 'Confirm', cancelText: 'Cancel', danger: false, ...opts };
  return mount(o);
}

// Information message (replaces window.alert in non-classic interface styles; does not block the page)
export function uiAlert(text) {
  const raw = String(text ?? '').replace(/\r/g, '').trim();
  const lines = raw.split('\n');
  let title = lines[0], message = lines.slice(1).join('\n').trim();
  if (!message && title.length > 90) { message = title; title = 'Message'; }
  const bad = /\b(error|failed|could not|cannot|can't|not allowed|invalid|denied|blocked|missing|required)\b/i.test(raw);
  return mount({ title, message, confirmText: 'OK', alertOnly: true, danger: bad });
}

let _installed = false;
export function installAlertDialog() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  const native = window.alert.bind(window);
  window.alert = (msg) => { if (isModernUi()) { uiAlert(msg); return undefined; } return native(msg); };
}
